import './extension-methods/date-extensions';

import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { LOCALE_ID, ModuleWithProviders, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { INDDButtonsI18nServiceConfigResources, NDDButtonsI18nServiceConfig } from '@ndk/ng-buttons';
import { NDDCoreModule } from '@ndk/ng-core';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { INDDDialogI18nServiceConfigResources, NDDDialogI18nServiceConfig } from '@ndk/ng-dialog';
import { NdkEmptyStatesModule } from '@ndk/ng-empty-states';
import { INDDFormI18nServiceConfigResources, NDDFormI18nServiceConfig } from '@ndk/ng-form';
import { INDDGridI18nServiceConfigResources, NDDGridBuilderService, NDDGridI18nServiceConfig } from '@ndk/ng-grid';
import { I18nModule } from '@ndk/ng-i18n';
import { INDDLightGridI18nServiceConfigResources, NDDLightGridI18nServiceConfig } from '@ndk/ng-light-grid';
import { NDDNavbarModule } from '@ndk/ng-navbar';
import { INDDNotificationI18nServiceConfigResources, NDDNotificationI18nServiceConfig, NDDNotificationItemService, NDDNotificationModule } from '@ndk/ng-notification';
import { NDDOffSidebarModule } from '@ndk/ng-offsidebar';
import { INDDReportI18nServiceConfigResources, NDDReportI18nServiceConfig } from '@ndk/ng-reports/component/shared/ndd-ng-reports-i18n.service';
import { NDDSidebarModule } from '@ndk/ng-sidebar';
import { NDDSpinnerModule } from '@ndk/ng-spinner';
import { LocalStorageService, NdkStorageModule } from '@ndk/ng-storage';
import { NDDTranslationConfig, NDDTranslationCoreModule, NDDTranslationKeys, NDDTranslationModule } from '@ndk/ng-translation';
import { PopupService } from '@progress/kendo-angular-popup';
import * as kendoui from '@progress/kendo-ui/js/kendo.treelist.js';
import { INDDKendoTranslationDatePickerI18nServiceConfigResources, INDDKendoTranslationGridI18nServiceConfigResources, INDDKendoTranslationTimePickerI18nServiceConfigResources, NDDI18nKendoTranslationDatePickerServiceConfig, NDDI18nKendoTranslationGridServiceConfig, NDDI18nKendoTranslationTimePickerServiceConfig } from 'ndd-ng-kendo-translation';
import * as toastr from 'toastr/toastr.js';

import { HelpMenuModule } from '@features/help-menu/help-menu.module';
import { UserMenuModule } from '@features/user-menu/user-menu.module';
import { INDDEditorI18nServiceConfigResources, NDDEditorI18nServiceConfig } from '@shared/ndd-ng-editor/shared/ndd-ng-editor-i18n.service';
import { INDDFilterStartupConfig } from '@shared/ndd-ng-filter/shared/models/ndd-ng-filter-config.model';
import { NDDFilterI18nServiceConfig } from '@shared/ndd-ng-filter/shared/ndd-ng-filter-i18n.service';
import { NDDFormsModule } from '@shared/ndd-ng-forms/ndd-ng-forms.module';
import { INDDFormsI18nServiceConfigResources, NDDFormsI18nServiceConfig } from '@shared/ndd-ng-forms/shared/ndd-ng-forms-i18n.service';
import { INDDSettingsI18nServiceConfigResources, NDDSettingsI18nServiceConfig } from '@shared/ndd-ng-settings/shared/ndd-ng-settings-i18n.service';
import { CustomIntlService } from '@shared/overrides/custom-intl.service';

import { AuthModule } from './authentication/auth.module';
import { CheckBrowserService } from './check-browser/check-browser.service';
import { CORE_CONFIG } from './core.config';
import { CustomValidatorsService } from './custom-validators/custom-validators.service';
import { DownloadService } from './download/download.service';
import { EnterpriseService } from './enterprise/enterprise.service';
import { GridExportService } from './grid-export/grid-export.service';
import { KENDO_TOKEN } from './kendo/kendo.value';
import { LayoutComponent } from './layout/layout.component';
import { LoggerService } from './logger/logger.service';
import { TOASTR_TOKEN } from './logger/toastr.token';
import { NotificationService } from './notification/notification.service';
import { PartnerParametersSaveTonerLicensingGuardService } from './partner-parameters/guards/partner-parameters-save-toner-licensing-guard.service';
import { PartnerParametersScanFlowGuardService } from './partner-parameters/guards/partner-parameters-scan-flow-guard.service';
import { PartnerParametersService } from './partner-parameters/partner-parameters.service';
import { LocalStorageKeys } from './storage/local-storage.enum';
import { DateUtilsService } from './utils/date.utils.service';
import { ExternalAddressService } from './utils/external-address.service';
import { RegexPatternService } from './utils/regex-pattern.service';
import { UrlLanguageReplaceService } from './utils/url-language-replace';
import { UtilsService } from './utils/utils.service';
import { RedirectToInternalServerHandler } from './error-interceptors/internal-server-error';

interface ICoreConfigResources {
    resources: { api: string };
    filter: INDDFilterStartupConfig;
    // Translation
    buttons: INDDButtonsI18nServiceConfigResources;
    forms: INDDFormsI18nServiceConfigResources;
    ndkForm: INDDFormI18nServiceConfigResources;
    dialog: INDDDialogI18nServiceConfigResources;
    grid: INDDGridI18nServiceConfigResources;
    settings: INDDSettingsI18nServiceConfigResources;
    editor: INDDEditorI18nServiceConfigResources;
    notification: INDDNotificationI18nServiceConfigResources;
    lightGrid: INDDLightGridI18nServiceConfigResources;
    reports: INDDReportI18nServiceConfigResources;
    // Kendo Translation
    kendoGrid: INDDKendoTranslationGridI18nServiceConfigResources;
    kendoDatePicker: INDDKendoTranslationDatePickerI18nServiceConfigResources;
    kendoTimePicker: INDDKendoTranslationTimePickerI18nServiceConfigResources;
}

@NgModule({
    imports: [
        CommonModule,
        RouterModule,

        HttpClientModule,

        NDDCoreModule,
        NDDFormsModule,
        NDDNavbarModule,
        NDDSidebarModule,
        NDDOffSidebarModule,
        NDDTranslationModule,
        NDDTranslationCoreModule,
        NdkStorageModule,
        I18nModule,
        UserMenuModule,
        HelpMenuModule,
        NDDNotificationModule,
        NDDSpinnerModule,
        NdkEmptyStatesModule,
        AuthModule,
    ],
    exports: [LayoutComponent],
    declarations: [
        LayoutComponent,
    ],
    providers: [
        // Tokens
        { provide: CORE_CONFIG_TOKEN, useValue: CORE_CONFIG },
        { provide: TOASTR_TOKEN, useValue: toastr },
        { provide: KENDO_TOKEN, useFactory: (): any => kendoui },
        {
            provide: LOCALE_ID,
            deps: [Window, LocalStorageService],
            useFactory: (windowRef: Window, localStorageService: LocalStorageService): string => {
                // Define a cultura atual para números e datas.
                return localStorageService.getValue(<any>CustomIntlService.LOCAL_STORAGE_CULTURE_KEY) || windowRef.navigator.language;
            },
        },

        UtilsService,
        DateUtilsService,
        UrlLanguageReplaceService,
        RegexPatternService,
        LoggerService,
        CustomValidatorsService,
        PopupService,
        CheckBrowserService,
        EnterpriseService,
        NDDGridBuilderService,
        NotificationService,
        NDDNotificationItemService,
        GridExportService,
        DownloadService,
        PartnerParametersService,
        PartnerParametersScanFlowGuardService,
        PartnerParametersSaveTonerLicensingGuardService,
        ExternalAddressService,
        RedirectToInternalServerHandler,
    ],
})
export class CoreModule {
    /**
     * Realiza configurações iniciais.
     * @param config - Configurações das resources para traduções dos componentes.
     */
    public static forRoot(config: ICoreConfigResources): ModuleWithProviders<CoreModule> {
        return {
            ngModule: CoreModule,
            providers: [
                {
                    provide: NDDTranslationConfig,
                    useValue: {
                        api: config.resources.api,
                        keys: <NDDTranslationKeys>{ currentLangKey: LocalStorageKeys.CurrentLanguage, resourcesKey: LocalStorageKeys.Resources },
                    },
                },
                { provide: INDDFilterStartupConfig, useValue: config.filter },
                // Translation
                { provide: NDDButtonsI18nServiceConfig, useValue: { i18n: true, resources: config.buttons } },
                { provide: NDDFilterI18nServiceConfig, useValue: { i18n: true, resources: config.filter.resources } },
                { provide: NDDFormsI18nServiceConfig, useValue: { i18n: true, resources: config.forms } },
                { provide: NDDFormI18nServiceConfig, useValue: { i18n: true, resources: config.ndkForm } },
                { provide: NDDDialogI18nServiceConfig, useValue: { i18n: true, resources: config.dialog } },
                { provide: NDDGridI18nServiceConfig, useValue: { i18n: true, resources: config.grid } },
                { provide: NDDSettingsI18nServiceConfig, useValue: { i18n: true, resources: config.settings } },
                { provide: NDDEditorI18nServiceConfig, useValue: { i18n: true, resources: config.editor } },
                { provide: NDDNotificationI18nServiceConfig, useValue: { i18n: true, resources: config.notification } },
                { provide: NDDLightGridI18nServiceConfig, useValue: { i18n: true, resources: config.lightGrid } },
                { provide: NDDReportI18nServiceConfig, useValue: { i18n: true, resources: config.reports } },
                // Kendo Translation
                { provide: NDDI18nKendoTranslationGridServiceConfig, useValue: { i18n: true, resources: config.kendoGrid } },
                { provide: NDDI18nKendoTranslationDatePickerServiceConfig, useValue: { i18n: true, resources: config.kendoDatePicker } },
                { provide: NDDI18nKendoTranslationTimePickerServiceConfig, useValue: { i18n: true, resources: config.kendoTimePicker } },
            ],
        };
    }
}
